import { ModalProps, Modal, Button, Stack, Switch, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { apiApplicants } from "@/api/recruit/applicant.api.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiApplicant } from "@/types/recruit/applicant.types.ts";
import { ApiApplicantCV } from "@/types/recruit/applicant.cv.types.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";

export interface ApplicantUploadCVModalProps extends ModalProps {
  applicant: ApiApplicant;
  cv: ApiApplicantCV;
  type: "pdf" | "docx";
}

export function ApplicantUploadCVModal({
  applicant,
  cv,
  type,
  ...props
}: ApplicantUploadCVModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const [hideSalary, setHideSalary] = useState(false);
  const [hideOther, setHideOther] = useState(false);

  const { isExporting, downloadFile } = useDownloadHelper();

  async function handleDownloadFile() {
    downloadFile({
      apiCall: () => apiApplicants.downloadCV(applicant.id, cv.id, type, hideSalary, hideOther),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.applicants.cvDownloaded, {
          [analyticProperties.id]: applicant.id,
          [analyticProperties.format]: type,
        });
        close(id);
      },
    });
  }

  return (
    <Modal
      {...props}
      title={type == "pdf" ? t("ats:prepare_your_pdf") : t("ats:prepare_your_word_file")}
      caption={[
        [applicant.first_name, applicant.middle_name, applicant.last_name]
          .filter((name) => name?.trim())
          .join(" "),
        cv.source.title,
      ]
        .filter((item) => item?.trim())
        .join(" · ")}
      withCloser
      actions={
        <Button
          isLoading={isExporting}
          onClick={handleDownloadFile}
          size={"lg"}
          className={"w-full"}
        >
          {type == "pdf" ? t("common:download_pdf") : t("common:download")}
        </Button>
      }
    >
      <Stack gap={"xl"} className={"pt-4"}>
        <Switch
          checked={hideOther}
          onChange={(value) => {
            setHideOther(value);
          }}
          title={t("ats:cv_hide_other")}
          description={t("ats:cv_hide_all_description")}
        />

        <Switch
          checked={hideSalary}
          onChange={(value) => {
            setHideSalary(value);
          }}
          title={t("ats:cv_hide_salary")}
          description={t("ats:cv_hide_salary_description")}
        />
      </Stack>
    </Modal>
  );
}
